"use client";

declare global {
  interface Window {
    fbq?: (...args: any[]) => void;
  }
}

import { useState } from "react";
import { Button } from "@/app/components/ui/button";
import { Input } from "@/app/components/ui/input";
import { useToast } from "@/app/hooks/use-toast";
import React from "react";
import { Textarea } from "./ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { COUNTRIES } from "../lib/data";
import { Checkbox } from "./ui/checkbox"; // Assuming you have a Checkbox component
import { Label } from "./ui/label"; // Assuming you have a Label component
import { COUNTRY_CODES } from "../lib/countryCode";

const CTASection = () => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [country, setCountry] = useState("");
  const [query, setQuery] = useState("");
  const [consentContact, setConsentContact] = useState(false);
  const [marketingUpdates, setMarketingUpdates] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [countryCode , setCountryCode] = useState("+91")
  const [errors, setErrors] = useState({
    name: "",
    email: "",
    phone: "",
    country: "",
    query: "",
    consentContact: "",
  });
  const { toast } = useToast();

  const baseUrl = process.env.NEXT_PUBLIC_BASE_URL;

  const validateForm = () => {
    const newErrors = {
      name: "",
      email: "",
      phone: "",
      country: "",
      query: "",
      consentContact: "",
    };
    let isValid = true;

    if (!name.trim()) {
      newErrors.name = "Name is required";
      isValid = false;
    }
    if (!email.trim()) {
      newErrors.email = "Email is required";
      isValid = false;
    } else if (!/^\S+@\S+\.\S+$/.test(email)) {
      newErrors.email = "Email is invalid";
      isValid = false;
    }
    if (!phone.trim()) {
      newErrors.phone = "Phone is required";
      isValid = false;
    }
    if (!country.trim()) {
      newErrors.country = "Country is required";
      isValid = false;
    }
    
    if (!query.trim()) {
      newErrors.query = "Query is required";
      isValid = false;
    }
    
    if (!consentContact) {
      newErrors.consentContact = "You must consent to being contacted";
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

     if (typeof window !== "undefined" && window.fbq) {
    window.fbq('track', 'ContactFormSubmit');
  }

    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      const dataToSend = { 
        name, 
        mobile: `${countryCode}${phone}`, 
        email, 
        country, 
        query,
        consentContact,
        marketingUpdates,
        path: "contactus" 
      };
      const response = await fetch(`${baseUrl}/api/submitQuery`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(dataToSend),
      });

      if (!response.ok) throw new Error("Failed to submit user data");

      const result = await response.json();
      console.log("Data submitted successfully:", result);
      setShowSuccessPopup(true);
      setIsSubmitting(false);

      toast({
        title: "Success!",
        description: "Your message has been sent successfully.",
      });

      setName("");
      setEmail("");
      setPhone("");
      setCountry("");
      setQuery("");
      setConsentContact(false);
      setMarketingUpdates(false);
    } catch (error) {
      console.error("Error submitting user data:", error);
      setIsSubmitting(false);
      toast({
        title: "Error",
        description:
          "There was a problem sending your message. Please try again.",
        variant: "destructive",
      });
    }
  };

  return (
    <section id="contact" className="min-h-screen flex flex-col lg:flex-row">
      {/* LEFT IMAGE (hidden on mobile) */}
      <div
        className="hidden lg:flex lg:w-1/2 bg-cover bg-center"
        style={{
          backgroundImage: "url('/contact.jpg')",
          backgroundAttachment: "fixed",
        }}
      >
        <div className="bg-black/50 w-full flex flex-col items-center justify-center text-center p-12">
          <h2 className="text-4xl font-bold text-white mb-4">
            Ready to transform your business?
          </h2>
          <p className="text-gray-200 text-lg max-w-md">
            Take the first step toward implementing solutions that deliver real
            results for your organization.
          </p>
        </div>
      </div>

      {/* RIGHT FORM */}
      <div className="w-full lg:w-1/2 flex items-center justify-center bg-gray-900 py-12 px-6">
        <div className="max-w-lg w-full bg-gray-800/50 backdrop-blur-md p-8 rounded-3xl shadow-xl border border-gray-700">
          <h3 className="text-2xl font-bold mb-6 text-white flex items-center">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-6 w-6 mr-2 text-cyan-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
              />
            </svg>
            Contact Us
          </h3>

          <form onSubmit={handleSubmit} className="space-y-4">
            <Input
              type="text"
              placeholder="Your name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className={`h-12 rounded-xl bg-gray-700/70 text-white placeholder-gray-400 border ${
                errors.name
                  ? "border-red-500 ring-2 ring-red-500"
                  : "border-gray-600"
              }`}
            />
            {errors.name && (
              <p className="text-red-500 text-xs flex items-center">
                {errors.name}
              </p>
            )}

            <Input
              type="email"
              placeholder="Email address"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className={`h-12 rounded-xl bg-gray-700/70 text-white placeholder-gray-400 border ${
                errors.email
                  ? "border-red-500 ring-2 ring-red-500"
                  : "border-gray-600"
              }`}
            />
            {errors.email && (
              <p className="text-red-500 text-xs flex items-center">
                {errors.email}
              </p>
            )}

            {/* <Input
              type="tel"
              placeholder="Phone number"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              className={`h-12 rounded-xl bg-gray-700/70 text-white placeholder-gray-400 border ${
                errors.phone
                  ? "border-red-500 ring-2 ring-red-500"
                  : "border-gray-600"
              }`}
            />
            {errors.phone && (
              <p className="text-red-500 text-xs flex items-center">
                {errors.phone}
              </p>
            )} */}

            <div>
  <div
    className={`flex items-center rounded-xl bg-gray-700/70 border ${
      errors.phone
        ? "border-red-500 ring-2 ring-red-500"
        : "border-gray-600"
    }`}
  >
  <Select value={countryCode} onValueChange={setCountryCode}>
  <SelectTrigger className="h-12 w-[110px] rounded-l-xl bg-gray-700/70 text-white border-r border-gray-600 focus:outline-none">
    <SelectValue placeholder="+91" />
  </SelectTrigger>
  <SelectContent className="bg-gray-800 text-white border-gray-700 max-h-60 overflow-y-auto scrollbar-hide">
    {COUNTRY_CODES?.map((c, index) => (
      <SelectItem key={`country-code-${index}`} value={c.code}>
        {c.flag} {c.code}
      </SelectItem>
    ))}
  </SelectContent>
</Select>

    <input
      type="tel"
      placeholder="Phone number"
      value={phone}
      onChange={(e) => setPhone(e.target.value)}
      className="h-12 flex-1 px-4 bg-transparent text-white placeholder-gray-400 rounded-r-xl focus:outline-none"
    />
  </div>

  {errors.phone && (
    <p className="text-red-500 text-xs flex items-center mt-1">
      {errors.phone}
    </p>
  )}
</div>


            <Select value={country} onValueChange={setCountry}>
              <SelectTrigger
                className={`h-12 rounded-xl bg-gray-700/70 text-white border ${
                  errors.country
                    ? "border-red-500 ring-2 ring-red-500"
                    : "border-gray-600"
                }`}
              >
                <SelectValue placeholder="Select Country" />
              </SelectTrigger>
              <SelectContent className="bg-gray-800 text-white border-gray-700 max-h-60 overflow-y-auto">
                {COUNTRIES?.map((country: any, index: number) => (
                  <SelectItem key={`country-${index}`} value={country.value}>
                    {country.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.country && (
              <p className="text-red-500 text-xs flex items-center">
                {errors.country}
              </p>
            )}

            <Textarea
              placeholder="Write your query here"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className={`min-h-[100px] rounded-xl bg-gray-700/70 text-white placeholder-gray-400 border ${
                errors.query
                  ? "border-red-500 ring-2 ring-red-500"
                  : "border-gray-600"
              }`}
            />
            {errors.query && (
              <p className="text-red-500 text-xs flex items-center">
                {errors.query}
              </p>
            )}

            {/* Consent Checkbox */}
           <div className="flex items-start space-x-2 pt-2">
  <Checkbox
    id="consentContact"
    checked={consentContact}
    onCheckedChange={(checked) => setConsentContact(checked === true)}
    className={`mt-1 data-[state=checked]:bg-cyan-600 data-[state=checked]:text-white ${
      errors.consentContact ? "border-red-500" : "border-gray-400"
    }`}
  />
  <div className="grid gap-1.5 leading-none">
    <Label
      htmlFor="consentContact"
      className="text-sm font-medium leading-none text-white peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
    >
      I consent to being contacted by GenAIPulse in response to my inquiry.
    </Label>
    {errors.consentContact && (
      <p className="text-red-500 text-xs flex items-center">
        {errors.consentContact}
      </p>
    )}
  </div>
</div>

{/* Marketing Updates Checkbox */}
<div className="flex items-start space-x-2 pt-2">
  <Checkbox
    id="marketingUpdates"
    checked={marketingUpdates}
    onCheckedChange={(checked) => setMarketingUpdates(checked === true)}
    className="mt-1 data-[state=checked]:bg-cyan-600 data-[state=checked]:text-white border-gray-400"
  />
  <div className="grid gap-1.5 leading-none">
    <Label
      htmlFor="marketingUpdates"
      className="text-sm font-medium leading-none text-white peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
    >
      I would like to receive marketing news and updates from GenAIPulse
    </Label>
  </div>
</div>

            <Button
              type="submit"
              disabled={isSubmitting}
              className="w-full h-12 bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-700 hover:to-blue-700 text-white font-semibold rounded-xl mt-4"
            >
              {isSubmitting ? "Submitting..." : "Submit"}
            </Button>
          </form>
        </div>
      </div>
      
      {showSuccessPopup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full p-6 mx-auto shadow-xl">
            <div className="flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mx-auto mb-4">
              <svg className="h-6 w-6 text-green-600" stroke="currentColor" fill="none" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 text-center mb-2">Success!</h3>
            <p className="text-gray-600 text-center mb-6">
              Your query was submitted successfully. We shall be in touch with you shortly!
            </p>
            <div className="text-center">
              <button
                onClick={() => setShowSuccessPopup(false)}
                className="px-4 py-2 bg-blue-600 text-white text-base font-medium rounded-md shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default CTASection;